/**
 * Orders applied filters atoms
 *
 * This file contains Jotai atoms for managing the orders applied filters state.
 */
import { atom } from 'jotai';

import {
	formatFinancingOfferStatus,
	formatPaymentStatus,
	labelFromPaymentChoice
} from '@ekhodealer/ekho-common';

import type { AppliedFilterItem } from '@common/components/FiltersDrawer';
import type {
	OrderPaymentChoice,	PurchasedVehicleFinancingOfferStatus,
	VehiclePaymentStatus} from '@ekhodealer/ekho-common';

import { formatCommonDateFilterLabel } from '@common/lib/filters';
import { formatTimestampToDate } from '@common/lib/time';
import { CommonDateFilter } from '@common/types/filters';

import { DeliveryDateFilter } from '../types/filters';
import { formatDeliveryDateFilterLabel } from '../utils/filters';

import {
	actionRequiredFilterAtom,
	financingStatusFilterAtom,
	fulfillmentDateCustomRangeAtom,
	fulfillmentDateFilterAtom,
	fulfillmentTypeFilterAtom,
	modelFilterAtom,
	orderDateCustomRangeAtom,
	orderDateFilterAtom,
	paymentChoiceFilterAtom,
	paymentStatusFilterAtom,
	registrationStateFilterAtom,
	searchValueAtom,
	typeFilterAtom,
} from './ordersAtoms';

/**
 * Derived atom that combines all filter values to create a comprehensive list
 * of applied filters for the chips display
 */
export const allAppliedFiltersAtom = atom((get): AppliedFilterItem[] => {
	const appliedFilters: AppliedFilterItem[] = [];

	// Get all filter values
	const searchValue = get(searchValueAtom);
	const actionRequired = get(actionRequiredFilterAtom);
	const paymentChoices = get(paymentChoiceFilterAtom);
	const paymentStatuses = get(paymentStatusFilterAtom);
	const financingStatuses = get(financingStatusFilterAtom);
	const models = get(modelFilterAtom);
	const types = get(typeFilterAtom);
	const registrationState = get(registrationStateFilterAtom);
	const orderDateFilter = get(orderDateFilterAtom);
	const fulfillmentType = get(fulfillmentTypeFilterAtom);
	const fulfillmentDateFilter = get(fulfillmentDateFilterAtom);

	// Add search query filter
	if (searchValue.trim()) {
		appliedFilters.push({
			id: 'search-query',
			title: 'Search',
			value: `"${searchValue.trim()}"`,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add action required filter
	if (actionRequired) {
		appliedFilters.push({
			id: 'action-required',
			title: 'Action required',
			value: 'Yes',
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add payment choice filters - create individual chip for each selection
	if (paymentChoices.length > 0) {
		paymentChoices.forEach((choice) => {
			appliedFilters.push({
				id: `payment-choice-${choice}`,
				title: 'Payment choice',
				value: labelFromPaymentChoice(choice as OrderPaymentChoice) || 'Unknown',
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add payment status filters - create individual chip for each selection
	if (paymentStatuses.length > 0) {
		paymentStatuses.forEach((status) => {
			appliedFilters.push({
				id: `payment-status-${status}`,
				title: 'Payment status',
				value: formatPaymentStatus(status as VehiclePaymentStatus),
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add financing status filters - create individual chip for each selection
	if (financingStatuses.length > 0) {
		financingStatuses.forEach((status) => {
			appliedFilters.push({
				id: `financing-status-${status}`,
				title: 'Financing status',
				value: formatFinancingOfferStatus(status as PurchasedVehicleFinancingOfferStatus),
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add model filters - create individual chip for each selection
	if (models.length > 0) {
		models.forEach((model) => {
			appliedFilters.push({
				id: `model-${model}`,
				title: 'Model',
				value: model,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add type filters - create individual chip for each selection
	if (types.length > 0) {
		types.forEach((type) => {
			appliedFilters.push({
				id: `type-${type}`,
				title: 'Type',
				value: type,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add registration state filter (single selection)
	if (registrationState) {
		appliedFilters.push({
			id: 'registration-state',
			title: 'Registration state',
			value: registrationState,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add order date filter (single selection or custom range)
	if (orderDateFilter) {
		if (orderDateFilter === CommonDateFilter.CUSTOM) {
			const customRange = get(orderDateCustomRangeAtom);
			// Only show chip when from is set (matches API filtering behavior)
			if (customRange.from !== null) {
				const fromStr = formatTimestampToDate(customRange.from) ?? '...';
				const toStr = formatTimestampToDate(customRange.to) ?? '...';
				appliedFilters.push({
					id: 'order-date',
					title: 'Order date',
					value: `${fromStr} - ${toStr}`,
					clearFunction: () => {
						// This will be set by the component using the atom
					},
				});
			}
		} else {
			appliedFilters.push({
				id: 'order-date',
				title: 'Order date',
				value: formatCommonDateFilterLabel(orderDateFilter),
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		}
	}

	// Add fulfillment filter (type + optional date)
	if (fulfillmentType) {
		const fulfillmentTypeLabel = fulfillmentType === 'delivery' ? 'Delivery' : 'Pickup';
		let value = fulfillmentTypeLabel;
		if (fulfillmentDateFilter) {
			if (fulfillmentDateFilter === DeliveryDateFilter.CUSTOM) {
				const customRange = get(fulfillmentDateCustomRangeAtom);
				// Only append date range when from is set (matches API filtering behavior)
				if (customRange.from !== null) {
					const fromStr = formatTimestampToDate(customRange.from) ?? '...';
					const toStr = formatTimestampToDate(customRange.to) ?? '...';
					value = `${fulfillmentTypeLabel}: ${fromStr} - ${toStr}`;
				}
			} else {
				value = `${fulfillmentTypeLabel}: ${formatDeliveryDateFilterLabel(
					fulfillmentDateFilter,
				)}`;
			}
		}
		appliedFilters.push({
			id: 'fulfillment',
			title: 'Fulfillment',
			value,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	return appliedFilters;
});

/**
 * Derived atom for the count of active filters
 */
export const activeFiltersCountAtom = atom((get) => {
	const allFilters = get(allAppliedFiltersAtom);
	return allFilters.length;
});
